<template>
    <div v-if="visible" class="material-update-overlay" @click.self="closeDialog">
        <section class="material-update-dialog" :class="{ 'material-update-dialog--makeup': professionalType === 'makeupArtist' }" role="dialog" aria-modal="true" aria-labelledby="materialUpdateTitle">
            <header>
                <div><p>{{ isEquipment ? 'EQUIPMENT REVIEW APPLICATION' : 'CERTIFICATE REVIEW APPLICATION' }}</p><h3 id="materialUpdateTitle">{{ title }}</h3><span>本次只提交{{ resourceDescription }}；提交后该类资料进入独立审核，不影响其他资料新增。</span></div>
                <button type="button" aria-label="关闭资料新增" @click="closeDialog">×</button>
            </header>
            <form novalidate @submit.prevent="submitUpdate">
                <main>
                    <section class="material-update-section">
                        <header><h4>{{ title }}</h4><button type="button" :aria-label="`添加${resourceDescription}`" @click="addItem">+添加</button></header>
                        <p>{{ isEquipment ? '填写设备类型、品牌型号并上传至少一张 JPG / PNG 证明图片。' : '证书名称与 JPG、PNG 或 PDF 原件需要同时提交。' }}</p>
                        <article v-for="(item, index) in form.items" :key="item.localId" class="material-update-item">
                            <button type="button" class="material-update-remove" :aria-label="`移除新增${resourceDescription} ${index + 1}`" @click="removeItem(index)">×</button>
                            <template v-if="isEquipment">
                                <label><span>设备类型 <b>*</b></span><input v-model.trim="item.equipmentType" maxlength="100" placeholder="例如：相机" /></label>
                                <label><span>品牌型号 <b>*</b></span><input v-model.trim="item.brandModel" maxlength="255" placeholder="例如：Sony A7M4" /></label>
                                <div class="material-update-image-field">
                                    <professional-application-image-upload
                                        label="设备证明"
                                        accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                                        required
                                        :max-count="1"
                                        :tone="uploadTone"
                                        :files="item.proofImages"
                                        :preview-urls="item.proofPreviewUrls"
                                        hint="支持 JPG / PNG，单张最大 5 MiB。"
                                        @select="selectEquipmentProof(item, $event)"
                                        @remove="removeEquipmentProof(item, $event)" />
                                </div>
                            </template>
                            <template v-else>
                                <label><span>证书名称 <b>*</b></span><input v-model.trim="item.name" maxlength="255" placeholder="例如：专业资格证" /></label>
                                <div class="material-update-certificate-field">
                                    <professional-application-file-field
                                        label="证书原件"
                                        accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
                                        required
                                        hint="支持 JPG / PNG / PDF，单个最大 5 MiB"
                                        :tone="uploadTone"
                                        :file="item.file"
                                        @select="selectCertificateFile(item, $event)"
                                        @remove="clearCertificateFile(item)" />
                                </div>
                            </template>
                        </article>
                    </section>
                    <p v-if="formError" class="material-update-error" role="alert">{{ formError }}</p>
                </main>
                <footer><button type="button" class="material-update-secondary" :disabled="submitting" @click="closeDialog">取消</button><button type="submit" class="material-update-primary" :disabled="submitting">{{ submitting ? '正在提交…' : '提交审核' }}</button></footer>
            </form>
        </section>
    </div>
</template>

<script>
import request from "@/tool/request";
import API from "@/tool/api";
import { getResultMessage, isResultSuccess } from "@/tool/result";
import ProfessionalApplicationFileField from "./ProfessionalApplicationFileField";
import ProfessionalApplicationImageUpload from "./ProfessionalApplicationImageUpload";
import {
    appendUploadFile,
    removeUploadFile,
} from "@/tool/professionalApplicationUpload.cjs";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png"];
const CERTIFICATE_TYPES = IMAGE_TYPES.concat("application/pdf");
const MAX_EQUIPMENT_PROOF_COUNT = 1;
let itemSequence = 0;
const nextId = (prefix) => `${prefix}-${itemSequence += 1}`;
const createEquipment = () => ({ localId: nextId("equipment"), equipmentType: "", brandModel: "", proofImages: [], proofPreviewUrls: [] });
const createCertificate = () => ({ localId: nextId("certificate"), name: "", file: null });

export default {
    name: "ProfessionalMaterialUpdateDialog",
    components: { ProfessionalApplicationFileField, ProfessionalApplicationImageUpload },
    props: { visible: { type: Boolean, default: false }, professionalType: { type: String, required: true }, resourceKind: { type: String, required: true } },
    data() { return { form: { items: [] }, formError: "", submitting: false }; },
    computed: {
        isEquipment() { return this.resourceKind === "equipment"; },
        resourceDescription() { return this.isEquipment ? "摄影设备" : "专业证书"; },
        title() { return `新增${this.resourceDescription}`; },
        professionalApi() { return API.creatorCenter[this.professionalType]; },
        uploadTone() { return this.professionalType === "makeupArtist" ? "makeup" : "photographer"; },
        submitEndpoint() { return this.isEquipment ? this.professionalApi.equipmentMaterialUpdates : this.professionalApi.certificateMaterialUpdates; },
    },
    watch: { visible(nextVisible) { if (nextVisible) this.resetForm(); } },
    beforeDestroy() { this.revokeAllEquipmentProofPreviews(); },
    methods: {
        resetForm() { this.revokeAllEquipmentProofPreviews(); this.form = { items: [] }; this.formError = ""; this.addItem(); },
        closeDialog() { if (!this.submitting) this.$emit("close"); },
        addItem() { this.form.items.push(this.isEquipment ? createEquipment() : createCertificate()); },
        removeItem(index) {
            this.revokeEquipmentProofPreviews(this.form.items[index]);
            this.form.items.splice(index, 1);
        },
        selectEquipmentProof(equipment, event) {
            const file = event && event.target && event.target.files && event.target.files[0];
            if (!file) return;
            if (equipment.proofImages.length >= MAX_EQUIPMENT_PROOF_COUNT) { this.formError = "每台设备只能上传 1 张证明图。"; event.target.value = ""; return; }
            if (IMAGE_TYPES.indexOf(file.type) < 0 || file.size > MAX_FILE_SIZE) { this.formError = "设备证明仅支持不超过 5 MiB 的 JPG 或 PNG 图片。"; event.target.value = ""; return; }
            equipment.proofImages = appendUploadFile(equipment.proofImages, file, MAX_EQUIPMENT_PROOF_COUNT);
            equipment.proofPreviewUrls.push(URL.createObjectURL(file));
            this.formError = "";
            event.target.value = "";
        },
        removeEquipmentProof(equipment, index) {
            URL.revokeObjectURL(equipment.proofPreviewUrls[index]);
            equipment.proofPreviewUrls.splice(index, 1);
            equipment.proofImages = removeUploadFile(equipment.proofImages, index);
            this.formError = "";
        },
        revokeEquipmentProofPreviews(equipment) {
            if (!equipment) return;
            (equipment.proofPreviewUrls || []).forEach((url) => URL.revokeObjectURL(url));
        },
        revokeAllEquipmentProofPreviews() {
            (this.form.items || []).forEach(this.revokeEquipmentProofPreviews);
        },
        selectCertificateFile(certificate, event) {
            const file = event.target && event.target.files && event.target.files[0];
            if (!file) return;
            if (CERTIFICATE_TYPES.indexOf(file.type) < 0 || file.size > MAX_FILE_SIZE) { this.formError = "证书原件仅支持不超过 5 MiB 的 JPG、PNG 或 PDF 文件。"; certificate.file = null; event.target.value = ""; return; }
            certificate.file = file; this.formError = "";
        },
        clearCertificateFile(certificate) {
            certificate.file = null;
            this.formError = "";
        },
        validateForm() {
            if (!this.form.items.length) return `请至少新增一项${this.resourceDescription}。`;
            if (this.isEquipment && this.form.items.some((item) => !item.equipmentType || !item.brandModel || !item.proofImages.length)) return "每项新增设备都必须填写设备类型、品牌型号并上传证明图片。";
            if (!this.isEquipment && this.form.items.some((item) => !item.name || !item.file)) return "每项新增证书都必须填写名称并上传证书原件。";
            return "";
        },
        buildFormData() {
            const formData = new FormData();
            if (this.isEquipment) this.form.items.forEach((item, index) => { formData.append(`addedEquipments[${index}].equipmentType`, item.equipmentType); formData.append(`addedEquipments[${index}].brandModel`, item.brandModel); item.proofImages.forEach((file) => formData.append(`addedEquipments[${index}].proofImages`, file)); });
            else this.form.items.forEach((item, index) => { formData.append(`addedCertificates[${index}].name`, item.name); formData.append(`addedCertificates[${index}].file`, item.file); });
            return formData;
        },
        submitUpdate() {
            this.formError = this.validateForm();
            if (this.formError || this.submitting) return;
            this.submitting = true;
            request.post(this.submitEndpoint, this.buildFormData())
                .then((response) => { if (!isResultSuccess(response)) throw response; this.$message.success(getResultMessage(response, `${this.title}已提交审核。`)); this.$emit("submitted", this.resourceKind); this.$emit("close"); })
                .catch((error) => { this.formError = getResultMessage(error, "资料提交失败，请稍后重试。"); })
                .finally(() => { this.submitting = false; });
        },
    },
};
</script>

<style scoped>
.material-update-overlay { position: fixed; z-index: 75; inset: 0; display: grid; place-items: center; padding: 24px; background: rgba(32, 27, 41, .56); }.material-update-dialog { display: flex; width: min(680px, calc(100vw - 48px)); max-height: min(760px, calc(100vh - 48px)); flex-direction: column; overflow: hidden; color: #332d49; background: #fff; border: 1px solid #d9e9f1; border-radius: 20px; box-shadow: 0 24px 70px rgba(30, 42, 58, .28); }.material-update-dialog > header { display: flex; gap: 18px; align-items: flex-start; justify-content: space-between; padding: 19px 22px 16px; border-bottom: 1px solid #e7eff3; }.material-update-dialog header p { margin: 0 0 4px; color: #5591ae; font-size: 10px; font-weight: 800; letter-spacing: .12em; }.material-update-dialog h3 { margin: 0 0 5px; color: #3f3158; font-size: 20px; }.material-update-dialog header span { color: #746c7e; font-size: 12px; line-height: 1.6; }.material-update-dialog header button { display: grid; width: 30px; height: 30px; place-items: center; padding: 0; color: #5d6070; background: #f1f6f8; border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 22px; }.material-update-dialog form { display: flex; min-height: 0; flex: 1; flex-direction: column; }.material-update-dialog form main { display: grid; gap: 17px; min-height: 0; padding: 22px; overflow: auto; }.material-update-section { display: grid; gap: 10px; padding: 16px; background: #f9fbfc; border: 1px solid #e5eef2; border-radius: 13px; }.material-update-section > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }.material-update-section h4 { margin: 0; color: #4b6f83; font-size: 14px; }.material-update-section > header button { min-height: 30px; padding: 5px 9px; color: #3e7f9e; background: #fff; border: 1px solid #bcd9e6; border-radius: 7px; cursor: pointer; font: inherit; font-size: 11px; font-weight: 800; }.material-update-section > p { margin: 0; color: #857f76; font-size: 11px; line-height: 1.6; }.material-update-item { position: relative; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; padding: 13px; background: #fff; border: 1px solid #e1eaee; border-radius: 10px; }.material-update-item label { display: grid; gap: 6px; color: #5b5362; font-size: 11px; font-weight: 700; }.material-update-item b { color: #dc657f; }.material-update-item input[type="text"], .material-update-item input:not([type]) { min-height: 36px; padding: 7px 9px; border: 1px solid #dbe3e8; border-radius: 8px; font: inherit; }.material-update-file { grid-column: 1 / -1; }.material-update-file small { color: #857f76; font-size: 11px; font-weight: 400; line-height: 1.5; }.material-update-file input { max-width: 100%; font-size: 11px; }.material-update-remove { position: absolute; top: 7px; right: 7px; display: grid; width: 22px; height: 22px; place-items: center; padding: 0; color: #8b7f88; background: #f4f2f4; border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 17px; }.material-update-error { margin: 0; color: #a1495c; font-size: 12px; line-height: 1.6; }.material-update-dialog footer { display: flex; flex: 0 0 auto; justify-content: flex-end; gap: 9px; padding: 14px 22px; border-top: 1px solid #e7eff3; }.material-update-dialog footer button { min-height: 38px; padding: 8px 15px; border-radius: 9px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 800; }.material-update-secondary { color: #5b7382; background: #fff; border: 1px solid #c9dce5; }.material-update-primary { color: #fff; background: #4f90ae; border: 1px solid #4f90ae; }.material-update-dialog footer button:disabled { cursor: not-allowed; opacity: .65; }
.material-update-dialog input,
.material-update-dialog select,
.material-update-dialog textarea,
.material-update-dialog option {
    color: #514a5a;
    font-weight: 400;
}

.material-update-dialog input::placeholder,
.material-update-dialog textarea::placeholder {
    color: #a8a3ad;
    font-weight: 400;
    opacity: 1;
}

.material-update-dialog input:focus,
.material-update-dialog select:focus,
.material-update-dialog textarea:focus {
    border-color: #dbe3e8;
    outline: 0;
    box-shadow: none;
}

.material-update-image-field {
    grid-column: 1 / -1;
    min-width: 0;
}

.material-update-certificate-field {
    grid-column: 1 / -1;
    min-width: 0;
}

.material-update-section > header button {
    display: inline-flex;
    min-width: 62px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
}

.material-update-dialog input,
.material-update-dialog select,
.material-update-dialog textarea,
.material-update-dialog option {
    font-weight: 400 !important;
}

.material-update-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
}

.material-update-upload-box {
    display: flex;
    min-height: 92px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    color: #7e8790;
    background: #fbfcfd;
    border: 1px dashed #bad8e6;
    border-radius: 9px;
    cursor: pointer;
    text-align: center;
}

.material-update-upload-box i {
    display: grid;
    width: 28px;
    height: 28px;
    flex: 0 0 auto;
    place-items: center;
    color: #4f90ae;
    background: #fff;
    border: 1px solid #bad8e6;
    border-radius: 7px;
    font-size: 21px;
    font-style: normal;
    font-weight: 400;
    line-height: 1;
}

.material-update-upload-box small {
    color: #857f76;
    font-size: 11px;
    font-weight: 400;
    line-height: 1.6;
    word-break: break-word;
}

.material-update-dialog > header > button,
.material-update-remove {
    line-height: 1 !important;
    text-align: center;
}
.material-update-dialog--makeup { border-color: #efc8d3; }
.material-update-dialog--makeup > header, .material-update-dialog--makeup footer { border-color: #f3dde3; }
.material-update-dialog--makeup header p, .material-update-dialog--makeup .material-update-section h4 { color: #b85d76; }
.material-update-dialog--makeup .material-update-section { border-color: #f1dce2; background: #fffafb; }
.material-update-dialog--makeup .material-update-section > header button { color: #b85d76; border-color: #edbdc9; }
.material-update-dialog--makeup .material-update-item { border-color: #f0dce2; }
.material-update-dialog--makeup .material-update-item input[type="text"], .material-update-dialog--makeup .material-update-item input:not([type]) { border-color: #ebc7d1; }
.material-update-dialog--makeup input:focus, .material-update-dialog--makeup select:focus, .material-update-dialog--makeup textarea:focus { border-color: #d56886; }
.material-update-dialog--makeup .material-update-secondary { color: #a95570; border-color: #edbdc9; }
.material-update-dialog--makeup .material-update-primary { background: #d56886; border-color: #d56886; }
.material-update-dialog--makeup .material-update-upload-box { border-color: #edbdc9; background: #fffafb; }
.material-update-dialog--makeup .material-update-upload-box > i { color: #c66a83; border-color: #edbdc9; }
</style>
